import { apiClient } from './client';

interface UploadResult {
  url: string;
  filename: string;
  originalName: string;
  size: number;
  mimeType: string;
}

/**
 * Derive the backend root URL from the API base URL.
 * VITE_API_BASE_URL = "http://localhost:5435/api" → "http://localhost:5435"
 */
function getBackendRootUrl(): string {
  const apiBase =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
    (typeof process !== 'undefined' && process.env?.VITE_API_BASE_URL) ||
    '/api';
  return apiBase.replace(/\/api\/?$/, '') || '';
}

export const uploadService = {
  /**
   * Upload an image file. Returns the full public URL for the image.
   */
  async uploadImage(file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<{ success: boolean; data: UploadResult }>(
      '/uploads/image',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    const { url, filename } = response.data.data;
    // Construct the full public URL from the relative path
    const fullUrl = `${getBackendRootUrl()}${url}`;

    return { url: fullUrl, filename };
  },

  /**
   * Delete a previously uploaded image by filename.
   */
  async deleteImage(filename: string): Promise<void> {
    await apiClient.delete(`/uploads/image/${encodeURIComponent(filename)}`);
  },

  /**
   * Extract the filename from a full upload URL.
   * e.g., "http://localhost:5435/uploads/1234-abcd.jpg" → "1234-abcd.jpg"
   * Returns null if the URL is not a local upload.
   */
  extractFilename(url: string): string | null {
    const match = url.match(/\/uploads\/([^/?#]+)$/);
    return match ? match[1] : null;
  },
};
